import React, { useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import {
  AccountNameFields,
  AccountPhoneField,
} from "../userForms/AccountDetailsFields.js";
import { hasPhoneNumber } from "./userProfiles.js";

export default function AccountSetup({
  profile = {},
  error = null,
  onComplete,
  onRetry,
  onSignOut,
}) {
  const [localError, setLocalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionPending = useRef(false);
  const disabledFields = ["firstName", "lastName", "phoneNumber"].filter(
    (field) =>
      field === "phoneNumber"
        ? hasPhoneNumber(profile[field])
        : typeof profile[field] === "string" && profile[field].trim() !== "",
  );

  async function handleSubmit(event) {
    event.preventDefault();
    if (submissionPending.current) {
      return;
    }

    const fields = event.currentTarget.elements;
    const details = {
      firstName: fields.namedItem("firstName").value.trim(),
      lastName: fields.namedItem("lastName").value.trim(),
      phoneNumber: fields.namedItem("phoneNumber").value.trim(),
    };
    if (!details.firstName || !details.lastName) {
      setLocalError("Enter your first and last name to continue.");
      return;
    }

    if (!hasPhoneNumber(details.phoneNumber)) {
      setLocalError("Enter your phone number to continue.");
      return;
    }

    submissionPending.current = true;
    setIsSubmitting(true);
    setLocalError(null);
    try {
      await onComplete(details);
    } catch {
      submissionPending.current = false;
      setIsSubmitting(false);
      setLocalError("We couldn't save your information. Please try again.");
    }
  }

  async function handleSignOut() {
    setLocalError(null);
    try {
      await onSignOut();
    } catch {
      setLocalError("We couldn't sign you out. Please try again.");
    }
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box my={6}>
        <Typography component="h1" variant="h5" gutterBottom>
          Finish setting up your account
        </Typography>
        {error ? (
          <>
            <Typography role="alert" color="error" paragraph>
              We couldn't load your account information. Please try again.
            </Typography>
            <Button variant="contained" color="primary" onClick={onRetry}>
              Retry
            </Button>
          </>
        ) : (
          <>
            <Typography paragraph>
              Add the missing information below to finish setting up your
              account. You can update your saved details on your account page.
            </Typography>
            <Typography paragraph>Email: {profile.email}</Typography>
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <AccountNameFields
                  defaultValues={profile}
                  disabled={isSubmitting}
                  disabledFields={disabledFields}
                />
                <AccountPhoneField
                  defaultValue={profile.phoneNumber}
                  disabled={
                    isSubmitting || disabledFields.includes("phoneNumber")
                  }
                  required
                />
              </Grid>
              <Box mt={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving…" : "Continue"}
                </Button>
              </Box>
            </form>
          </>
        )}
        {localError && (
          <Box mt={2}>
            <Typography role="alert" color="error">
              {localError}
            </Typography>
          </Box>
        )}
        <Box mt={2}>
          <Button onClick={handleSignOut}>Sign out</Button>
        </Box>
      </Box>
    </Container>
  );
}
